/**
 * Issues Page - Main dashboard for data quality issues with lineage context
 * 
 * This page provides a comprehensive view of all data quality issues,
 * organized by priority and showing business impact through lineage.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
  UserGroupIcon,
  ChartBarIcon,
  ArrowPathIcon,
  PlusIcon,
  FunnelIcon,
  BarsArrowUpIcon,
  BarsArrowDownIcon,
  BellSlashIcon
} from '@heroicons/react/24/outline';
import SnoozeModal from '../../components/notifications/SnoozeModal';
import lineageApiService from '../../api/lineageApiService';
import { useUI } from '../../contexts/UIContext';
import { apiRequest } from '../../utils/apiUtils';
import ImpactBadge from '../../components/issues/ImpactBadge';
import { metadataIntegrationService } from '../../services/metadataIntegrationService';

const IssuesPage = () => {
  const navigate = useNavigate();
  const { updateBreadcrumbs } = useUI();
  const [issues, setIssues] = useState({ issues: {}, summary: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'open',
    severity: 'all',
    priority: 'all',
    assignee: 'all',
    database: 'all', // Phase 3.8: Database filter
    schema: 'all' // Phase 3.8: Schema filter
  });
  const [sortBy, setSortBy] = useState('priority'); // 'priority', 'assignee', 'created_at'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [refreshing, setRefreshing] = useState(false);
  const [users, setUsers] = useState([]);
  const [affectingMe, setAffectingMe] = useState(false); // Filter for issues affecting the current user
  const [tableMetadata, setTableMetadata] = useState({}); // Phase 3.8: Table metadata with tier status
  const [databases, setDatabases] = useState([]); // Phase 3.8: Available databases
  const [schemas, setSchemas] = useState([]); // Phase 3.8: Available schemas
  const [schemasByDatabase, setSchemasByDatabase] = useState({}); // Phase 3.8: Schema lookup by database
  const [snoozeTarget, setSnoozeTarget] = useState(null); // For snooze modal

  useEffect(() => {
    // Clear breadcrumbs to use path-based breadcrumbs for Issues page
    updateBreadcrumbs([]);
    loadIssues();
    loadUsers();
  }, [updateBreadcrumbs, affectingMe]); // Reload when affectingMe filter changes

  const loadUsers = async () => {
    try {
      const response = await apiRequest('/profiles');
      if (response.success) {
        setUsers(response.users || []);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // Phase 3.8: Helper to parse database.schema.table from full table name
  const parseFullTableName = (fullName) => {
    if (!fullName) return { database: null, schema: null, table: null };
    const parts = fullName.split('.');

    // Standard format: DATABASE.SCHEMA.TABLE (3 parts)
    if (parts.length === 3) {
      return {
        database: parts[0],
        schema: parts[1],
        table: parts[2]
      };
    }

    // Fallback for legacy 2-part format: SCHEMA.TABLE
    if (parts.length === 2) {
      return {
        database: null,
        schema: parts[0],
        table: parts[1]
      };
    }

    return { database: null, schema: null, table: null };
  };

  // Phase 3.8: Load table metadata for issues
  const loadTableMetadata = async (issuesList) => {
    try {
      // Extract unique connection IDs from issues
      const connectionIds = new Set();

      issuesList.forEach(issue => {
        if (issue.source_object && issue.source_object.connection_id) {
          connectionIds.add(issue.source_object.connection_id);
        }
      });

      // Fetch metadata for each connection
      const metadataPromises = Array.from(connectionIds).map(async (connectionId) => {
        try {
          const response = await metadataIntegrationService.getTableMetadataTiered(connectionId, {}, {});
          return { connectionId, data: response };
        } catch (error) {
          console.error(`Error loading metadata for connection ${connectionId}:`, error);
          return { connectionId, data: null };
        }
      });

      const results = await Promise.allSettled(metadataPromises);

      // Build metadata lookup by connection and table
      const metadata = {};
      const uniqueDatabases = new Set();
      const uniqueSchemas = new Set();
      const schemasByDb = {};

      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value.data && result.value.data.success) {
          const { connectionId, data } = result.value;
          metadata[connectionId] = {};

          (data.tables || []).forEach(table => {
            const database = table.table_catalog || table.database_name;
            const schema = table.table_schema;
            const tableName = table.table_name;

            // Use database.schema.table as key
            const tableKey = `${database}.${schema}.${tableName}`;
            metadata[connectionId][tableKey] = {
              tierStatus: data.tierStatus,
              database: database,
              schema: schema,
              tableName: tableName,
              tier1Complete: table.tier1_collected_at != null,
              tier2Complete: table.tier2_collected_at != null,
              tier3Complete: table.tier3_collected_at != null,
              tier4Complete: table.tier4_collected_at != null
            };

            // Track unique databases and schemas
            if (database) {
              uniqueDatabases.add(database);

              if (!schemasByDb[database]) {
                schemasByDb[database] = new Set();
              }
              if (schema) {
                schemasByDb[database].add(schema);
                uniqueSchemas.add(schema);
              }
            }
          });
        }
      });

      // Convert schema sets to arrays
      const schemasByDatabaseObj = {};
      Object.keys(schemasByDb).forEach(db => {
        schemasByDatabaseObj[db] = Array.from(schemasByDb[db]).sort();
      });

      setTableMetadata(metadata);
      setDatabases(Array.from(uniqueDatabases).sort());
      setSchemas(Array.from(uniqueSchemas).sort());
      setSchemasByDatabase(schemasByDatabaseObj);
    } catch (error) {
      console.error('Error loading table metadata:', error);
    }
  };

  const loadIssues = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const queryParams = {};
      if (affectingMe) {
        queryParams.affecting_me = 'true';
      }

      // Load issues with affecting_me filter if enabled
      const response = await lineageApiService.getIssues(queryParams);

      // Handle the response - it should already have the data structure we need
      if (response && response.data) {
        setIssues(response.data);

        // Phase 3.8: Load table metadata for schema filtering and tier status
        const allIssues = [
          ...(response.data.issues.high_priority || []),
          ...(response.data.issues.medium_priority || []),
          ...(response.data.issues.low_priority || [])
        ];
        await loadTableMetadata(allIssues);
      } else if (response && response.success === false) {
        setError(response.error || 'Failed to load issues');
      } else {
        // Default structure if response is unexpected
        setIssues({
          issues: {
            high_priority: [],
            medium_priority: [],
            low_priority: []
          },
          summary: {
            total_issues: 0,
            open_issues: 0,
            critical_issues: 0,
            resolved_issues: 0
          }
        });
      }
    } catch (err) {
      console.error('Error loading issues:', err);
      setError(err.message);
      // Set default structure on error
      setIssues({
        issues: {
          high_priority: [],
          medium_priority: [],
          low_priority: []
        },
        summary: {
          total_issues: 0,
          open_issues: 0,
          critical_issues: 0,
          resolved_issues: 0
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadIssues();
    setRefreshing(false);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleCreateIssue = () => {
    navigate('/issues/new');
  };

  const handleViewIssue = (issue) => {
    navigate(`/issues/${issue.id}`);
  };


  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'high': return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
      case 'medium': return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'low': return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      default: return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { bg: 'bg-red-100', text: 'text-red-800', label: 'Open' },
      investigating: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Investigating' },
      resolved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Resolved' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Closed' },
      false_positive: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'False Positive' }
    };

    const config = statusConfig[status] || statusConfig.open;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // Phase 3.8: Helper to get tier status badge
  const getTierStatusBadge = (issue) => {
    const { database, schema, table } = parseFullTableName(issue.source_object?.full_name);
    if (!database || !schema || !table || !issue.source_object?.connection_id) return null;

    const connectionId = issue.source_object.connection_id;
    const tableKey = `${database}.${schema}.${table}`;
    const metadata = tableMetadata[connectionId]?.[tableKey];

    if (!metadata) return null;

    // Count completed tiers
    const completedTiers = [
      metadata.tier1Complete,
      metadata.tier2Complete,
      metadata.tier3Complete,
      metadata.tier4Complete
    ].filter(Boolean).length;

    if (completedTiers === 0) return null;

    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
        Tier {completedTiers}/4
      </span>
    );
  };

  // Client-side filtering function
  const filterIssues = (issuesList) => {
    return issuesList.filter(issue => {
      if (filters.status !== 'all' && issue.status !== filters.status) return false;
      if (filters.severity !== 'all' && issue.severity !== filters.severity) return false;
      if (filters.assignee !== 'all') {
        if (filters.assignee === 'unassigned') {
          if (issue.assigned_to || issue.assigned_to_team_id) return false;
        } else {
          if (issue.assigned_to !== filters.assignee) return false;
        }
      }
      // Phase 3.8: Database and schema filtering
      const { database, schema } = parseFullTableName(issue.source_object?.full_name);
      if (filters.database !== 'all' && database !== filters.database) return false;
      if (filters.schema !== 'all' && schema !== filters.schema) return false;
      return true;
    });
  };

  // Sorting function
  const sortIssues = (issuesList) => {
    const sorted = [...issuesList];
    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'priority':
          comparison = (b.priority_score || 0) - (a.priority_score || 0);
          break;
        case 'assignee':
          const aAssignee = a.assigned_user ? `${a.assigned_user.first_name} ${a.assigned_user.last_name}`.trim() : a.assigned_team || 'Unassigned';
          const bAssignee = b.assigned_user ? `${b.assigned_user.first_name} ${b.assigned_user.last_name}`.trim() : b.assigned_team || 'Unassigned';
          comparison = aAssignee.localeCompare(bAssignee);
          break;
        case 'created_at':
          comparison = new Date(b.created_at) - new Date(a.created_at);
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? -comparison : comparison;
    });
    return sorted;
  };

  // Combine all issues from all priority groups
  const allIssues = [
    ...(issues.issues.high_priority || []),
    ...(issues.issues.medium_priority || []),
    ...(issues.issues.low_priority || [])
  ];

  // Apply filters and sorting
  const filteredAndSortedIssues = sortIssues(filterIssues(allIssues));

  const renderIssueCard = (issue) => (
    <div key={issue.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            {getSeverityIcon(issue.severity)}
            <h3 className="text-lg font-medium text-gray-900">{issue.title}</h3>
            {getStatusBadge(issue.status)}

            {/* Assignee Badge */}
            {issue.assigned_user && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                <UserGroupIcon className="h-3 w-3" />
                {issue.assigned_user.first_name} {issue.assigned_user.last_name}
              </span>
            )}
            {issue.assigned_team_details && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                <UserGroupIcon className="h-3 w-3" />
                {issue.assigned_team_details.name}
              </span>
            )}
            {!issue.assigned_user && !issue.assigned_team_details && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                Unassigned
              </span>
            )}
          </div>

          {/* Business Impact Badges */}
          {issue.business_impact_badges && (
            <div className="mb-3">
              <ImpactBadge
                impactData={issue.business_impact_badges}
                size="sm"
                layout="horizontal"
                showTooltip={true}
              />
            </div>
          )}

          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
            {issue.description}
          </p>

          <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
            <div className="flex items-center gap-2">
              <span>Table: {issue.source_object?.object_name || 'Unknown'}</span>
              {/* Phase 3.8: Show tier status */}
              {getTierStatusBadge(issue)}
            </div>
            <span>Priority: {issue.priority_score?.toFixed(1)}/100</span>
            <span>Created: {new Date(issue.created_at).toLocaleDateString()}</span>
          </div>
          
          {issue.business_impact_summary && (
            <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Business Impact:</strong> {issue.business_impact_summary}
              </p>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm text-gray-500">
              {issue.estimated_user_impact > 0 && (
                <div className="flex items-center gap-1">
                  <UserGroupIcon className="h-4 w-4" />
                  <span>{issue.estimated_user_impact} users affected</span>
                </div>
              )}
              {issue.business_processes_affected?.length > 0 && (
                <div className="flex items-center gap-1">
                  <ChartBarIcon className="h-4 w-4" />
                  <span>{issue.business_processes_affected.length} processes</span>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSnoozeTarget({
                    type: 'table',
                    id: issue.source_object?.object_name || issue.id,
                    name: issue.source_object?.object_name || issue.title,
                    connectionId: issue.source_object?.connection_id
                  });
                }}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700"
                title="Snooze alerts for this table"
              >
                <BellSlashIcon className="h-4 w-4" />
                Snooze
              </button>
              <button
                onClick={() => handleViewIssue(issue)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                <EyeIcon className="h-4 w-4" />
                View Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading issues...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Data Quality Issues</h1>
          <p className="text-gray-600 mt-1">
            Monitor and resolve data quality issues with full business impact context
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAffectingMe(!affectingMe)}
            className={`inline-flex items-center gap-2 px-4 py-2 border rounded-md shadow-sm text-sm font-medium transition-colors ${
              affectingMe
                ? 'border-purple-500 bg-purple-50 text-purple-700 hover:bg-purple-100'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <UserGroupIcon className="h-4 w-4" />
            {affectingMe ? 'Showing: Issues Affecting Me' : 'Show Issues Affecting Me'}
          </button>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          <button
            onClick={handleCreateIssue}
            className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4" />
            Create Issue
          </button>
        </div>
      </div>

      {/* Affecting Me Callout */}
      {affectingMe && (
        <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <UserGroupIcon className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-900">
                  Issues Affecting You
                </h3>
                <p className="text-sm text-purple-700 mt-1">
                  Showing {filteredAndSortedIssues.length} {filteredAndSortedIssues.length === 1 ? 'issue' : 'issues'} that impact data objects assigned to your stakeholder groups.
                  {filteredAndSortedIssues.length === 0 && " Great news! No issues are currently affecting your data."}
                </p>
              </div>
            </div>
            <button
              onClick={() => setAffectingMe(false)}
              className="text-purple-600 hover:text-purple-800 text-sm font-medium"
            >
              Clear Filter
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Open Issues</p>
              <p className="text-3xl font-bold text-red-600">{issues.summary.open_issues || 0}</p>
            </div>
            <ClockIcon className="h-8 w-8 text-red-400" />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Critical Issues</p>
              <p className="text-3xl font-bold text-orange-600">{issues.summary.critical_issues || 0}</p>
            </div>
            <ExclamationTriangleIcon className="h-8 w-8 text-orange-400" />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Resolved Issues</p>
              <p className="text-3xl font-bold text-green-600">{issues.summary.resolved_issues || 0}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-400" />
          </div>
        </div>
      </div>

      {/* Filters and Sorting */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between gap-4">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>

            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="text-sm border border-gray-300 rounded px-3 py-1"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="investigating">Investigating</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>

            <select
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
              className="text-sm border border-gray-300 rounded px-3 py-1"
            >
              <option value="all">All Severity</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <select
              value={filters.assignee}
              onChange={(e) => handleFilterChange('assignee', e.target.value)}
              className="text-sm border border-gray-300 rounded px-3 py-1"
            >
              <option value="all">All Assignees</option>
              <option value="unassigned">Unassigned</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.first_name && user.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user.email}
                </option>
              ))}
            </select>

            {/* Phase 3.8: Database filter */}
            <select
              value={filters.database}
              onChange={(e) => {
                handleFilterChange('database', e.target.value);
                // Reset schema filter when database changes
                if (e.target.value !== filters.database) {
                  handleFilterChange('schema', 'all');
                }
              }}
              className="text-sm border border-gray-300 rounded px-3 py-1"
            >
              <option value="all">All Databases</option>
              {databases.map(database => (
                <option key={database} value={database}>
                  {database}
                </option>
              ))}
            </select>

            {/* Phase 3.8: Schema filter - filtered by selected database */}
            <select
              value={filters.schema}
              onChange={(e) => handleFilterChange('schema', e.target.value)}
              className="text-sm border border-gray-300 rounded px-3 py-1"
              disabled={filters.database === 'all'}
            >
              <option value="all">All Schemas</option>
              {(filters.database !== 'all'
                ? (schemasByDatabase[filters.database] || [])
                : schemas
              ).map(schema => (
                <option key={schema} value={schema}>
                  {schema}
                </option>
              ))}
            </select>
          </div>

          {/* Sort Controls */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border border-gray-300 rounded px-3 py-1"
            >
              <option value="priority">Priority</option>
              <option value="assignee">Assignee</option>
              <option value="created_at">Created Date</option>
            </select>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-1 hover:bg-gray-100 rounded"
              title={sortOrder === 'asc' ? 'Sort Ascending' : 'Sort Descending'}
            >
              {sortOrder === 'asc' ? (
                <BarsArrowUpIcon className="h-5 w-5 text-gray-600" />
              ) : (
                <BarsArrowDownIcon className="h-5 w-5 text-gray-600" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
            <p className="text-red-800">Error loading issues: {error}</p>
          </div>
        </div>
      )}

      {/* Issues List */}
      {filteredAndSortedIssues.length > 0 ? (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Issues ({filteredAndSortedIssues.length})
          </h2>
          <div className="grid grid-cols-1 gap-4">
            {filteredAndSortedIssues.map(renderIssueCard)}
          </div>
        </div>
      ) : null}

      {/* Empty State */}
      {!loading && !error && filteredAndSortedIssues.length === 0 && (
        <div className="text-center py-12">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No issues found</h3>
          <p className="mt-1 text-sm text-gray-500">
            No data quality issues match your current filters.
          </p>
          <div className="mt-6">
            <button
              onClick={handleCreateIssue}
              className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4" />
              Create New Issue
            </button>
          </div>
        </div>
      )}

      {/* Snooze Modal */}
      <SnoozeModal
        isOpen={!!snoozeTarget}
        onClose={() => setSnoozeTarget(null)}
        target={snoozeTarget}
        onSnoozeCreated={() => {
          setSnoozeTarget(null);
          // Optionally show a success notification
        }}
      />
    </div>
  );
};

export default IssuesPage;